import { jwtDecode } from 'jwt-decode'

import {
    DEFAULT_MAIN_PAGE_DESKTOP_CATEGORY_SIZE,
    DEFAULT_MAIN_PAGE_MOBILE_CATEGORY_SIZE,
    DEFAULT_RECOMMENDATION_SIZE,
} from '@/constants/general'
import { AvailableLocale } from '@/i18n/helpers'
import {
    APICategoryDataType,
    APICategorySeoInfo,
    APICategoryShortInfo,
    APIGameInfoDTO,
    APIGameRecommendation,
    APIGeneralInfo,
    APIRankingInfo,
    CategoryDataType,
    CategoryType,
    ClientType,
    FormSubmitData,
    GameRecommendationType,
    PlatformType,
    User,
} from '@/types'
import { GoogleSearchAPIResult } from '@/types/search'

import { FetchRequest, FetchResult, GamesAndCategoriesRequestParams } from './types'

export interface IgpPurchase {
    id: string
    purchaseToken: string
    status: string
    externalId: string
    bridgeId?: string
    [key: string]: unknown
}

type PlaygamaConsentDetailsDTO = {
    authorization_id?: string
    redirect_uri?: string
    redirect_url?: string
    client?: {
        id?: string
        name?: string
    }
    scope?: string
    scopes?: string[]
}

export type PlaygamaConsentDetails = {
    appName: string
    authorizationId: string
    redirectTo?: string
    scopes: string[]
}

export type PlaygamaConsentLoadResult =
    | {
          status: 'consent-required'
          consentDetails: PlaygamaConsentDetails
      }
    | {
          status: 'resolved'
          redirectUrl: string
      }

type PlaygamaConsentDecisionDTO = {
    redirect_to?: string
    redirect_url?: string
}

const normalizePlaygamaConsentScopes = (data: PlaygamaConsentDetailsDTO) => {
    if (data.scopes) {
        return data.scopes
    }

    return data.scope?.split(/\s+/u).filter(Boolean)
}

export class ApiRequests {
    public request: FetchRequest
    public cmsRequest: FetchRequest

    constructor({ request, cmsRequest }: { request: FetchRequest; cmsRequest?: FetchRequest }) {
        this.request = request
        this.cmsRequest =
            cmsRequest ??
            (() => {
                throw new Error('cmsRequest is not configured')
            })
    }

    public submitContactForm = (data: FormSubmitData) => {
        return this.request('/api/v1/email/report', {
            method: 'POST',
            cache: 'no-store',
            body: JSON.stringify(data),
        })
    }

    public getGamesByCategories = async ({
        categoriesIds,
        platform,
        pagination,
        locale,
        withDebugInfo,
        pageType,
        pinnedGame,
        rankingClusterId,
        rankingAudience,
        includeUnsupportedPlatforms,
        scope,
    }: GamesAndCategoriesRequestParams): Promise<CategoryDataType> => {
        const path = '/api/v1/categories/games'
        const search = new URLSearchParams()
        search.set('pagination', JSON.stringify(pagination))
        search.set('platform', platform)
        search.set('locale', locale)
        search.set('categoriesIds', JSON.stringify(categoriesIds))

        if (rankingClusterId) {
            search.set('cluster_id', rankingClusterId)
        }
        if (rankingAudience) {
            search.set('audnc', rankingAudience)
        }

        if (pinnedGame) {
            search.set('pinned_game', pinnedGame)
        }

        if (withDebugInfo) {
            search.set('debug', 'true')
        }

        if (includeUnsupportedPlatforms) {
            search.set('include_unsupported_platforms', 'true')
        }

        if (pageType) {
            search.set('type', pageType)
        }

        if (scope) {
            search.set('scope', scope)
        }

        const { originalResponse, data } = await this.request<APICategoryDataType>(`${path}?${search}`)

        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }

        return {
            categories: data.categories.map((category) => ({
                ...category,
                games: category.games,
            })),
            pagination: data.pagination,
        }
    }

    public getMainPageGamesByCategories = async ({
        platform,
        locale,
        rankingClusterId,
        rankingAudience,
        withDebugInfo,
        scope,
    }: {
        platform: PlatformType
        locale: string
        rankingClusterId: string | undefined
        rankingAudience: string | undefined
        withDebugInfo: boolean
        scope?: string
    }): Promise<CategoryDataType> => {
        const path = '/api/v1/categories/main-page'
        const search = new URLSearchParams()
        search.set('platform', platform)
        search.set(
            'pageSize',
            platform === 'desktop'
                ? DEFAULT_MAIN_PAGE_DESKTOP_CATEGORY_SIZE.toString()
                : DEFAULT_MAIN_PAGE_MOBILE_CATEGORY_SIZE.toString(),
        )
        search.set('locale', locale)

        if (rankingClusterId) {
            search.set('cluster_id', rankingClusterId)
        }
        if (rankingAudience) {
            search.set('audnc', rankingAudience)
        }

        if (withDebugInfo) {
            search.set('debug', 'true')
        }

        if (scope) {
            search.set('scope', scope)
        }

        const { originalResponse, data } = await this.request<APICategoryDataType>(`${path}?${search}`)

        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }

        const gamesByCategories: APICategoryDataType = data
        return {
            categories: gamesByCategories.categories.map((category) => ({
                ...category,
                games: category.games,
            })),
            pagination: gamesByCategories.pagination,
        }
    }

    public getMainCategories = async (locale: string): Promise<APIGeneralInfo> => {
        const path = '/api/v1/general/info'
        const search = new URLSearchParams()
        search.set('locale', locale)

        const { originalResponse, data } = await this.request<APIGeneralInfo>(`${path}?${search}`)

        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }
        return data
    }

    public getCurrentUser = async (): Promise<User | null> => {
        if (typeof window === 'undefined') {
            throw new Error('Cannot get current user on SSR')
        }
        const path = '/api/v1/user/current'
        const search = new URLSearchParams()
        search.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)

        const { data, originalResponse } = await this.request<User>(`${path}?${search}`)

        if (!originalResponse.ok) {
            return null
        }

        return data || null
    }

    public getPlaygamaConsent = async (authorizationId: string): Promise<PlaygamaConsentLoadResult> => {
        const search = new URLSearchParams({
            authorization_id: authorizationId,
        })

        const { originalResponse, data, errors } = await this.request<PlaygamaConsentDetailsDTO>(
            `/api/v1/auth/playgama-oauth/consent?${search}`,
        )

        if (!originalResponse.ok || !data) {
            throw new Error(errors[0] || 'Unable to load Playgama authorization details')
        }

        if (data.redirect_url) {
            return {
                status: 'resolved',
                redirectUrl: data.redirect_url,
            }
        }

        const appName = data.client?.name
        const scopes = normalizePlaygamaConsentScopes(data)

        if (!appName || !data.authorization_id || !scopes?.length) {
            throw new Error(errors[0] || 'Unable to load Playgama authorization details')
        }

        return {
            status: 'consent-required',
            consentDetails: {
                appName,
                authorizationId: data.authorization_id,
                redirectTo: data.redirect_uri,
                scopes,
            },
        }
    }

    public submitPlaygamaConsentDecision = async ({
        authorizationId,
        decision,
    }: {
        authorizationId: string
        decision: 'approve' | 'deny'
    }) => {
        const { originalResponse, data, errors } = await this.request<PlaygamaConsentDecisionDTO>(
            '/api/v1/auth/playgama-oauth/consent',
            {
                method: 'POST',
                body: JSON.stringify({
                    authorization_id: authorizationId,
                    decision,
                }),
            },
        )

        const redirectTo = data?.redirect_to || data?.redirect_url

        if (!originalResponse.ok || !redirectTo) {
            throw new Error(errors[0] || 'Unable to submit Playgama authorization decision')
        }

        return {
            redirectTo,
        }
    }

    public getGameByParam = async ({
        hru,
        locale,
        context,
        showHidden,
        clientType,
    }: {
        hru: string
        locale: string
        context: 'portal' | 'export'
        showHidden?: boolean
        clientType?: ClientType
    }): Promise<APIGameInfoDTO> => {
        const path = `/api/v1/games/info/${hru}`
        const search = new URLSearchParams({
            locale,
            context,
        })
        if (showHidden) {
            search.append('show_hidden', 'true')
        }
        if (clientType) {
            search.append('client_type', clientType)
        }
        try {
            const { originalResponse, data } = await this.request<APIGameInfoDTO>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getRandomGameHRUs = async ({
        platform,
        amount,
    }: {
        platform: PlatformType
        amount?: number
    }): Promise<string[]> => {
        const path = '/api/v1/games/random-hrus'
        const search = new URLSearchParams()
        search.set('platform', platform)
        if (amount) {
            search.set('amount', amount.toString())
        }

        try {
            const { originalResponse, data } = await this.request<string[]>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getGamesCategoriesShortInfo = async ({
        type,
        locale,
    }: {
        type: CategoryType
        locale: string
    }): Promise<APICategoryShortInfo[]> => {
        const path = `/api/v1/categories/short-info/${type}`
        const search = new URLSearchParams()

        search.set('locale', locale)

        try {
            const { originalResponse, data } = await this.request<APICategoryShortInfo[]>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }
            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    public getGamesCategoriesSeoById = async ({
        id,
        locale,
        type,
    }: {
        id: string
        locale: AvailableLocale
        type: CategoryType
    }): Promise<APICategorySeoInfo> => {
        const path = `/api/v1/categories/seo/${id}`
        const search = new URLSearchParams()
        search.set('locale', locale)
        search.set('type', type)

        try {
            const { originalResponse, data } = await this.request<APICategorySeoInfo>(`${path}?${search}`)
            if (!originalResponse.ok || !data) {
                return Promise.reject(originalResponse.status)
            }
            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    // Decoder for signed jwt requests: decodes jwt body and attaches original data
    public signedRequest = async <T>(url: string, config: RequestInit = {}) => {
        const result = await this.request<string>(url, config)
        if (!result.data) {
            return result as FetchResult<T>
        }

        const { data: jwt } = result

        return {
            ...result,
            data: {
                jwt,
                ...jwtDecode<T>(jwt),
            },
        }
    }

    public getGameRecommendations = async <T extends GameRecommendationType[]>({
        hru,
        types,
        platform,
        locale,
        rankingClusterId,
        rankingAudience,
        limit = DEFAULT_RECOMMENDATION_SIZE,
    }: {
        hru: string
        types: T
        platform: PlatformType
        locale: string
        rankingClusterId: string | undefined
        rankingAudience: string | undefined
        limit?: number
    }): Promise<APIGameRecommendation<T>> => {
        const search = new URLSearchParams()
        search.set('rec_types', types.join(','))
        search.set('limit', String(limit))
        search.set('platform', platform)
        search.set('locale', locale)

        if (rankingClusterId) {
            search.set('cluster_id', rankingClusterId)
        }
        if (rankingAudience) {
            search.set('audnc', rankingAudience)
        }

        const URL = `/api/v1/games/recommendations/${hru}?${search}`
        try {
            const { originalResponse, data } = await this.request<APIGameRecommendation<T>>(URL)
            if (!originalResponse.ok || !data) {
                // maybe log the reason - it's not clear
                return Promise.reject(originalResponse.status)
            }

            return data
        } catch (error) {
            console.error({ URL, error })
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject(500)
        }
    }

    // these params are whitelisted on the portal-back
    public search = async (params: {
        q: string
        num: string
        // interface lang
        hl: string
        // restrict results only on this lang
        lr: string
        // user geolocation
        // gl: ...
        // siteSearch: ...
        // siteSearchFilter: 'i' | 'e'
        commit?: string // indicator for portal-back to do extra logic
    }): Promise<GoogleSearchAPIResult> => {
        const query = new URLSearchParams(params)
        const { data } = await this.request<GoogleSearchAPIResult>(`/api/v1/search/g?${query}`)
        if (data) {
            return data
        }
        throw new Error('Search error')
    }

    public confirmIgpDelivery = async ({
        gameId,
        orderId,
        externalId,
    }: {
        gameId: string
        orderId: string
        externalId: string
    }): Promise<void> => {
        const URL = '/api/v1/in-game-payments/confirm-delivery'
        const { originalResponse } = await this.request(URL, {
            method: 'POST',
            body: JSON.stringify({ gameId, orderId, externalId }),
        })
        if (!originalResponse.ok) {
            return Promise.reject(originalResponse.status)
        }
    }

    public getIgpPurchases = async (gameId: string): Promise<IgpPurchase[]> => {
        const search = new URLSearchParams({ gameId })
        const URL = `/api/v1/in-game-payments/purchases?${search}`
        const { originalResponse, data } = await this.request<IgpPurchase[]>(URL)
        if (!originalResponse.ok || !data) {
            return Promise.reject(originalResponse.status)
        }
        return data
    }

    public consumeIgpPurchase = async ({
        gameId,
        orderId,
        externalId,
    }: {
        gameId: string
        orderId: string
        externalId: string
    }): Promise<void> => {
        const URL = '/api/v1/in-game-payments/consume'
        const { originalResponse } = await this.request(URL, {
            method: 'POST',
            body: JSON.stringify({ gameId, orderId, externalId }),
        })
        if (!originalResponse.ok) {
            return Promise.reject(originalResponse.status)
        }
    }

    public blockAccount = async (): Promise<void> => {
        const { originalResponse } = await this.request('/api/v1/user/block-account', {
            method: 'POST',
        })
        if (!originalResponse.ok) {
            return Promise.reject(originalResponse.status)
        }
    }

    public getRankingClusterId = async ({
        auid,
        platform,
        audience = '',
        currentClusterId = '',
    }: {
        auid: string
        platform: PlatformType
        audience?: string
        currentClusterId?: string
    }): Promise<APIRankingInfo> => {
        const query = new URLSearchParams({
            auid,
            platform,
            audnc: audience,
            currentClusterId,
        })
        const { data } = await this.request<APIRankingInfo>(`/api/v1/user-clustering/cluster-id?${query}`)

        if (!data) {
            throw new Error('Unexpected empty ranking info')
        }
        return data
    }

    // Served by the portal express instance (src/server/cms/routes.ts), not portal-back.
    // Uses cmsRequest (no API_HOST prefix) so relative URLs work on the client and SSR
    // uses VUE_APP_HOST_URL as the host.
    public getGoogleProvidersEnabled = async (clid: string): Promise<boolean> => {
        try {
            const search = new URLSearchParams({ clid })
            const { data } = await this.cmsRequest<{ enabled: boolean }>(`/ads/google-providers-enabled?${search}`)

            return !!data?.enabled
        } catch {
            return false
        }
    }
}
